import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  SITE_SETTINGS_DEFAULT,
  PROJECTS, SERVICES, AMENITIES, TESTIMONIALS, FAQS, STORY,
} from "../mock";
import {
  fetchSite, updateSettings as apiUpdateSettings, updateStory as apiUpdateStory,
  upsertItem as apiUpsertItem, deleteItem as apiDeleteItem,
  submitContact,
} from "../lib/api";

const SettingsContext = createContext(null);

const defaultState = {
  settings: SITE_SETTINGS_DEFAULT,
  projects: PROJECTS,
  services: SERVICES,
  amenities: AMENITIES,
  testimonials: TESTIMONIALS,
  faqs: FAQS,
  story: STORY,
  submissions: [],
};

export function SettingsProvider({ children }) {
  const [state, setState] = useState(defaultState);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchSite();
      setState((s) => ({
        ...s,
        settings: { ...SITE_SETTINGS_DEFAULT, ...(data.settings || {}) },
        story:    { ...STORY, ...(data.story || {}) },
        projects: data.projects?.length ? data.projects : PROJECTS,
        services: data.services?.length ? data.services : SERVICES,
        amenities: data.amenities?.length ? data.amenities : AMENITIES,
        testimonials: data.testimonials?.length ? data.testimonials : TESTIMONIALS,
        faqs: data.faqs?.length ? data.faqs : FAQS,
      }));
    } catch (e) {
      // Fallback to defaults silently — page still renders
      // eslint-disable-next-line no-console
      console.warn("Falling back to mock data", e?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSettings = async (partial) => {
    const next = { ...state.settings, ...partial };
    setState((s) => ({ ...s, settings: next }));
    try { await apiUpdateSettings(next); } catch (e) { console.error(e); }
  };

  const updateStory = async (next) => {
    setState((s) => ({ ...s, story: next }));
    try { await apiUpdateStory(next); } catch (e) { console.error(e); }
  };

  const upsertItem = async (key, item) => {
    const list = state[key] || [];
    const optimistic = list.some((x) => x.id === item.id)
      ? list.map((x) => (x.id === item.id ? item : x))
      : [...list, item];
    setState((s) => ({ ...s, [key]: optimistic }));
    try {
      const saved = await apiUpsertItem(key, item);
      setState((s) => ({
        ...s,
        [key]: (s[key] || []).map((x) => (x.id === saved.id ? saved : x)).concat(
          (s[key] || []).some((x) => x.id === saved.id) ? [] : [saved]
        ).filter((v, i, a) => a.findIndex((y) => y.id === v.id) === i),
      }));
    } catch (e) { console.error(e); }
  };

  const removeFromList = async (key, id) => {
    setState((s) => ({ ...s, [key]: (s[key] || []).filter((x) => x.id !== id) }));
    try { await apiDeleteItem(key, id); } catch (e) { console.error(e); }
  };

  // Used by ContactPage; returns the saved submission
  const addSubmission = async (sub) => {
    const saved = await submitContact(sub);
    setState((s) => ({ ...s, submissions: [saved, ...(s.submissions || [])] }));
    return saved;
  };

  const setSubmissions = (list) => setState((s) => ({ ...s, submissions: list }));

  const reload = () => load();

  return (
    <SettingsContext.Provider value={{
      ...state,
      loaded,
      reload,
      updateSettings,
      updateStory,
      upsertItem,
      removeFromList,
      addSubmission,
      setSubmissions,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
